import MUIBox from '@/components/MUI-Components/MUI-Box';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import MUIGrid from '@/components/MUI-Components/MUI-Grid';
import MUITypography from '@/components/MUI-Components/MUI-Typography';
import styles from '@/styles/ourservices.module.scss';
import Typography from '@mui/material/Typography';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import {Fragment, memo} from 'react';
const DisplayImage = dynamic(() => import('@/components/Ui-components/display-image'));
import parse from 'html-react-parser';
import {AnimatedComponent} from '../AnimatedComponent';
import {fadeInUp} from '@/animation/scroll-animation';
import type {SxProps, Theme} from '@mui/material/styles';
import {spacing} from '@/styles/tokens';

// Migrated from globals.scss `.new-services-box` (exclusively owned).
const sxNewServicesBox = {
    display: 'flex',
    gap: spacing[12],
    alignItems: 'center'
};

// Migrated from globals.scss `.our-service-new-div` (+ `img` descendant, exclusively owned).
const sxOurServiceNewDiv = {
    height: '60px',
    maxWidth: '60px',
    '& img': {height: '100%'}
};

// Migrated from globals.scss `.non-link-box` (sole consumer). Applied only in the
// non-redirect branch below, matching the className's own conditional application.
const nonLinkBoxSx = {
    cursor: 'default !important'
} as const;

const renderOurServices = (data: any, className: any, xSx: any) => {
    return (
        <MUIBox
            className={
                data.redirectLink
                    ? `${styles.our_services_parent} ${className} card_main_div`
                    : `${styles.our_services_parent} ${className} card_main_div non-link-box `
            }
            sx={data.redirectLink ? xSx : ([nonLinkBoxSx, xSx] as SxProps<Theme>)}
        >
            <MUIBox sx={sxNewServicesBox}>
                <MUIBox sx={sxOurServiceNewDiv}>
                    <DisplayImage
                        src={data.icon}
                        alt={data.heading}
                        width={60}
                        height={60}
                        className={styles.helper_card_icon_services}
                        priority={false}
                    />
                </MUIBox>
                {data.redirectLink !== '' ? (
                    <Link href={data?.redirectLink} target="_blank" className={styles.services_link}>
                        <MUITypography component="h4" className={`${styles.expertise_card_heading} ${styles.color_black}`}>
                            {data.heading}
                        </MUITypography>
                    </Link>
                ) : (
                    <MUITypography component="h4" className={`${styles.expertise_card_heading} ${styles.color_black}`}>
                        {data.heading}
                    </MUITypography>
                )}
            </MUIBox>

            <p className={`${styles.expertise_content} ${styles.color_black} ${styles.data_links}`}>{data?.body ? parse(data.body) : ''}</p>
        </MUIBox>
    );
};
// `xSx`/`containerSx` are additive sx escape hatches for the card box / grid container,
// alongside the existing `className`/`containerClass` string passthroughs. Migrating this
// component's ~67 page consumers off global CSS is separate, long-tail work (Initiative 6).
const OurServices = (props: any) => {
    const {ourServices, className, heading, subheading, containerClass, xSx, containerSx} = props;
    return (
        <MUIGrid item xs={12} className={styles.expertise_parent}>
            <MUIContainer className={styles.services_container}>
                <MUIBox className={styles.solution_wrapper}>
                    <MUIBox className="spectrum-header-wrapper solutions">
                        <MUITypography className="spectrum-heading color-black our-service-main-heading text-align-left">
                            {heading}
                        </MUITypography>
                        <MUIBox className="divder-wrapper">
                            <hr className="divider-line divider-line-left" />
                        </MUIBox>
                        {subheading ? (
                            <MUIBox className="sub-heading-box">
                                <Typography className="spectrum-sub-heading color-black spacing text-align-left">
                                    {subheading ? parse(subheading) : ''}
                                </Typography>
                            </MUIBox>
                        ) : null}
                    </MUIBox>
                    <MUIGrid
                        container
                        spacing={2}
                        className={`${styles.our_services_main} ${containerClass}`}
                        sx={containerSx as SxProps<Theme>}
                    >
                        {ourServices?.map((data: any, index: number) => (
                            <Fragment key={index + 1}>
                                <MUIGrid item xs={12} sm={6} md={4} pt={subheading ? 4 : 1}>
                                    <AnimatedComponent variants={fadeInUp} delay={index * 0.2} className="height-full">
                                        {renderOurServices(data, className, xSx)}
                                    </AnimatedComponent>
                                </MUIGrid>
                            </Fragment>
                        ))}
                    </MUIGrid>
                </MUIBox>
            </MUIContainer>
        </MUIGrid>
    );
};
export default memo(OurServices);
